import { action as mobxAction } from 'mobx';
import { Container, Singleton } from 'typescript-ioc/es5';
import { message, notification } from 'antd';
import { DomainDto } from '../dtos/domain';
import { AppState } from '../stores/app';
import {
    arrayContains,
    fetchDomain, getCurrentDomainSession, getCurrentDomainStorage,
    setCurrentDomainSession, simpleNotification, titleCase,
} from '../_utils/utils';
import axios from 'axios';
import { PlatformMessageDto } from '../dtos/platform-message';
import * as querystring from 'query-string';
import * as CryptoJS from 'crypto-js';
import * as randomstring from 'randomstring';
import aqe from '@pushly/aqe';
import { SubjectEntity } from '../enums/ability-entity.enum';

@Singleton
export class AppService {

    public appContainerRef: any;
    public contentContainerRef: any;
    public platformMessageTimer: number;

    protected historyContainer: any;
    protected previousRoute: string;
    protected fetchingPlatformMessages: boolean = false;

    protected appState: AppState;

    public constructor() {
        this.appState = Container.get(AppState);

        message.config({
            top: 52,
            getContainer: this.getAppContainer
        });
        notification.config({
            top: 52,
            getContainer: this.getAppContainer
        });
    }

    public set404State() {
        this.appState.showing404Count += 1;
    }

    public unset404State() {
        this.appState.showing404Count = this.appState.showing404Count <= 0 ?
            0 :
            this.appState.showing404Count - 1;
    }

    public reset404State() {
        this.appState.showing404Count = 0;
    }

    public resetContentScrollPosition(): void {
        if (!!this.contentContainerRef) {
            this.contentContainerRef.scrollTo(0, 0);
        }
    }

    public getDomainDto(id: number) {
        return this.appState.currentUserDomains?.find(d => d.id === id);
    }

    public getAppContainer = (): HTMLElement => {
        return this.appContainerRef;
    };

    public attachHistoryContainer(container: any): void {
        if (!this.historyContainer) {
            this.historyContainer = container;
        }
    }

    public get currentRoute(): string {
        return this.historyContainer.location.pathname;
    };

    @mobxAction.bound
    public route(newRoute: string): void {
        this.previousRoute = this.currentRoute;

        if (this.appState.isVersionCurrent) {
            this.historyContainer.push(newRoute);
        } else {
            location.href = `${location.origin}/${newRoute.replace(/^\//, '')}`;
        }
    }

    @mobxAction.bound
    public routeWithinDomain(newRoute: string, buildOnly?: boolean): string {
        if (this.appState.currentDomain) {
            // sanitize and prepend domain
            newRoute = newRoute.replace(/^\/?(?:domain[s]?\/([\d]+\/)?)?/i, '');
            newRoute = `/domains/${this.appState.currentDomain!.id}/${newRoute}`;
        }

        if (buildOnly !== true) {
            this.previousRoute = this.currentRoute;
            if (this.appState.isVersionCurrent) {
                this.historyContainer.push(newRoute);
            } else {
                location.href = `${location.origin}/${newRoute.replace(/^\//, '')}`;
            }
        }

        return newRoute;
    }

    @mobxAction.bound
    public routeWithin(entity: 'org' | 'domain', newRoute: string, buildOnly?: boolean): string {
        const isOrg = entity === 'org';
        const entityPath = isOrg ? 'organizations' : 'domains';

        if (this.appState.currentDomain) {
            const domain = this.appState.currentDomain!;
            const entityId = isOrg ? domain.accountId : domain.id;

            // sanitize and prepend domain
            newRoute = newRoute.replace(/^\/?(?:(domain|organization)[s]?\/([\d]+\/)?)?/i, '');
            newRoute = `/${entityPath}/${entityId}/${newRoute}`;
        }

        if (buildOnly !== true) {
            this.previousRoute = this.currentRoute;
            if (this.appState.isVersionCurrent) {
                this.historyContainer.push(newRoute);
            } else {
                location.href = `${location.origin}/${newRoute.replace(/^\//, '')}`;
            }
        }

        return newRoute;
    }

    @mobxAction.bound
    public routeBack(): void {
        const newRoute = this.previousRoute;
        if (!newRoute || newRoute === this.currentRoute) {
            this.historyContainer.goBack();
            return;
        }
        this.route(newRoute);
    }

    @mobxAction.bound
    public setAppLoading(): void {
        this.appState.loadingCount = this.appState.loadingCount + 1;
    }

    @mobxAction.bound
    public unsetAppLoading(): void {
        if (this.appState.loadingCount === 0) {
            return;
        }

        this.appState.loadingCount = this.appState.loadingCount - 1;
    }

    @mobxAction.bound
    public setModuleLoading(): void {
        this.appState.moduleLoadingCount = this.appState.moduleLoadingCount + 1;
    }

    @mobxAction.bound
    public unsetModuleLoading(): void {
        if (this.appState.moduleLoadingCount === 0) {
            return;
        }

        this.appState.moduleLoadingCount = this.appState.moduleLoadingCount - 1;
    }

    @mobxAction.bound
    public addAndSetCurrentUserDomain(domain: DomainDto) {
        this.appState.currentUserDomains = [
            ...(this.appState.currentUserDomains || []),
            domain,
        ];

        this.appState.currentDomainJsonData = JSON.stringify(domain);
        setCurrentDomainSession(domain);
        this.updateDomainInPlatformPath(domain);
    }

    @mobxAction.bound
    public updateDomain(domain: DomainDto) {
        if (this.appState.currentUserDomains) {
            const index = this.appState.currentUserDomains.findIndex(d => d.id === domain.id);
            this.appState.currentUserDomains.splice(index, 1, domain);
        }
    }

    @mobxAction.bound
    public removeDomain(domain: DomainDto) {
        if (this.appState.currentUserDomains) {
            const index = this.appState.currentUserDomains.findIndex(d => d.id === domain.id);
            this.appState.currentUserDomains.splice(index, 1);
        }
    }

    @mobxAction.bound
    public setCurrentDomain(domain: DomainDto, manualSelection?: boolean, showing404?: boolean) {
        this.appState.currentDomainJsonData = JSON.stringify(domain);
        setCurrentDomainSession(domain);

        if (!showing404) {
            this.updateDomainInPlatformPath(domain);
        }

        if (manualSelection && this.appState.showing404Count > 1) {
            this.routeWithinDomain('/dashboard');
        }
    }

    public get domainIdFromPath(): string | undefined {
        let id: any;
        const idRequestRegx = /.*?\/domains\/([\d]+).*/i;

        if (this.historyContainer) {
            const path = this.historyContainer.location.pathname;
            if (idRequestRegx.test(path)) {
                id = path.replace(/.*?\/domains\/([\d]+).*/i, "$1");
            }
        }

        return id;
    }

    public handleBackButtonNavigation(prevProps, currProps) {
        const realPrevRoute = prevProps.location.pathname;
        const currHash = currProps.location.hash;
        const hash = prevProps.location.hash;

        // hash updates should not follow realPrevRoute by back button navigation due to
        // Tabs components using hash routing
        if (!!hash && hash !== currHash) {
            return;
        }

        if (realPrevRoute === this.currentRoute) {
            this.historyContainer.goBack();
        }
    }

    @mobxAction.bound
    public async synchronizeDomainStates() {
        const coldStorageDomain = getCurrentDomainStorage();
        const sessionDomain = getCurrentDomainSession();
        const requestedDomainId = this.domainIdFromPath;
        let invalidDomainRequest = false;

        this.setAppLoading();

        let requestedDomain: DomainDto | undefined;
        if (requestedDomainId) {
            const currentlyShowingId = sessionDomain?.id ?? coldStorageDomain?.id ?? undefined;
            if (requestedDomainId !== String(currentlyShowingId)) {
                try {
                    requestedDomain = await fetchDomain(requestedDomainId);
                } catch {
                    invalidDomainRequest = true;
                    this.set404State();
                }
            }
        }

        if (requestedDomain) {
            this.setCurrentDomain(requestedDomain, false, invalidDomainRequest);
        } else if (sessionDomain) {
            this.setCurrentDomain(sessionDomain, false, invalidDomainRequest);
        } else if (coldStorageDomain) {
            this.setCurrentDomain(coldStorageDomain, false, invalidDomainRequest);
        }

        this.unsetAppLoading();
    }

    @mobxAction.bound
    public toggleSidebar() {
        this.appState.sidebar.collapsed = !this.appState.sidebar.collapsed;
    }

    @mobxAction.bound
    public closeSidebar() {
        this.appState.sidebar.collapsed = true;
    }

    @mobxAction.bound
    public openSidebar() {
        this.appState.sidebar.collapsed = false;
    }

    @mobxAction.bound
    public setNotifListSourceFilters(domainId: number, filters: string | string[]): void {
        filters = Array.isArray(filters) ? filters : filters.split(',');
        filters = filters.map(f => f.toString().trim().toUpperCase())
                         .filter(f => !!f && f !== '');
        filters.sort();

        const currentFilters = this.getNotifListSourceFilters();
        currentFilters[domainId] = filters;

        this.appState.notifListSourceFilters = JSON.stringify(currentFilters);
    }

    public getNotifListSourceFilters(domainId?: number): any {
        let filters: any = this.appState.notifListSourceFilters ?? '{}';
        try { filters = JSON.parse(filters); }
        catch (_) { filters = {}; }

        if (domainId !== undefined) {
            filters = filters[domainId] ?? [];
        }

        return filters;
    }

    public startFetchingPlatformMessages(): void {
        if (!this.platformMessageTimer) {
             this.updatePlatformMessages();
        }
    }

    public stopFetchingPlatformMessages(): void {
        if (this.platformMessageTimer !== void 0) {
            clearTimeout(this.platformMessageTimer);
        }
    }

    public getCurrentPlatformMessages(): PlatformMessageDto[] {
        const user = this.appState.currentUser!;
        const seenMsgs = this.getUserPlatformMessagesFromStorage();
        let platformMsgs = this.appState.platformMessages || [];

        if (user) {
            // Filter already seen messages out
            platformMsgs = platformMsgs.filter(msg => !arrayContains(seenMsgs, `u${user.id}::${msg.id}`));
            // Filter remaining by domain visibility
            platformMsgs = platformMsgs.filter(msg => !msg.domainIds || arrayContains(msg.domainIds, this.appState.currentDomain!.id));
        }

        return platformMsgs;
    }

    public setPlatformMessageAsSeen(msgId: number): void {
        const user = this.appState.currentUser!;
        const seenMsgs = this.getUserPlatformMessagesFromStorage();
        const seenMsgKey = `u${user.id}::${msgId}`;

        this.appState.userPlatformMessages = JSON.stringify([
            ...seenMsgs.filter(key => key !== seenMsgKey),
            seenMsgKey,
        ]);
    }

    public resetUserPlatformMessages(): void {
        const user = this.appState.currentUser!;
        const seenMsgs = this.getUserPlatformMessagesFromStorage();
        const userIdRgx = new RegExp(`^u${user.id}::`);

        this.appState.userPlatformMessages = JSON.stringify([
            ...seenMsgs.filter(key => !userIdRgx.test(key)),
        ]);
    }

    public async fetchPlatformMessages(options?: any): Promise<PlatformMessageDto[]> {
        options = options || {};
        if (options.pagination === void 0) options.pagination = false;
        options = querystring.stringify(options || {});

        let messages: PlatformMessageDto[] = [];
        try {
            const req = await axios.get(`${aqe.defaults.publicApiDomain}/system/messages?${options}`);
            messages = req.data.data;
        } catch {
        }

        return messages;
    }

    public async createMessage(msg: PlatformMessageDto): Promise<boolean> {
        this.setModuleLoading();

        try {
            await axios.post(`${aqe.defaults.publicApiDomain}/system/messages`, msg);
            simpleNotification('success', `Message was successfully created`);
            this.unsetModuleLoading();
            return true;
        } catch (error) {
            simpleNotification('error', error.response.data.message);
            this.unsetModuleLoading();
            return false;
        }
    }

    public async destroyMessage(msg: PlatformMessageDto): Promise<boolean> {
        this.setModuleLoading();

        try {
            await axios.delete(`${aqe.defaults.publicApiDomain}/system/messages/${msg.id}`);
            simpleNotification('success', `Message was successfully removed`);
            this.unsetModuleLoading();
            return true;
        } catch (error) {
            simpleNotification('error', error.response.data.message);
            this.unsetModuleLoading();
            return false;
        }
    }

    public async getEventLogs(
        opts: any = {},
        showLoadingScreen: boolean = false,
    ): Promise<any> {
        if (showLoadingScreen) this.setModuleLoading();
        let res: any = { data: [], total: 0, count: 0 };

        try {
            const options = querystring.stringify(opts || {});
            const serviceURL = `${aqe.defaults.publicApiDomain}/system/event-logs${options}`;

            res = await axios.get(serviceURL);
            if (showLoadingScreen) this.unsetModuleLoading();

            return res.data;
        } catch (error) {
            simpleNotification('error', error.response.data.message);
            if (showLoadingScreen) this.unsetModuleLoading();

            return res;
        }
    }

    public calculateMediaHashFromUrl = async (
        url: string,
    ): Promise<string> => {
        const file = await fetch(url);
        const blob = await file.blob();

        const hash: any = await this.calculateMediaHashFromBlob(blob);

        return hash.toString();
    };

    public calculateMediaHashFromBlob = async (
        blob: Blob,
    ): Promise<string> => {
        const hash: any = await (new Promise(res => {
            const fileReader = new FileReader();

            fileReader.onload = (e: any) => {
                const data = CryptoJS.SHA256(CryptoJS.enc.Latin1.parse(e.target.result));
                res(data);
            };

            fileReader.readAsBinaryString(blob);
        }));

        return hash.toString();
    };

    public async checkStoredMediaUrl(
        url: string,
        entityType: 'ACCOUNT' | 'DOMAIN',
        entityId: number,
    ): Promise<any> {
        const response: any = { url };

        try {
            response.hash = await this.calculateMediaHashFromUrl(url);
        } catch (_err) {
            response.hash = randomstring.generate({length: 36});
        }

        try {
            const entityPath = `${entityType.toLowerCase()}s/${entityId}`;
            const res = await axios.get(`${aqe.defaults.publicApiDomain}/${entityPath}/media/${response.hash}`);
            response.data = res.data.data;
        } catch (_err) {
        }

        return response;
    }

    public async removeMediaPartial(
        handle: string,
    ): Promise<any> {
        try {
            const res = await axios.delete(`${aqe.defaults.publicApiDomain}/media/partial/${handle}`);
            return res.data;
        } catch (error) {
            simpleNotification('error', error.response.data.message);
        }
    }

    public customizeTabTitle = (): void => {
        let page: string = 'Pushly';

        const titleEl = document.querySelector("h2");
        if (titleEl) {
            page = titleEl!.textContent!;
        }

        if (this.appState.inDomainContext) {
            const currentDomain = this.appState.currentDomain;
            const domainDisplayName = currentDomain?.displayName;

            if (domainDisplayName) {
                document.title = `${domainDisplayName} - ${page}`;
            }
        }
        if (this.appState.inOrgContext) {
            const accountName = this.appState.currentDomain?.accountName;

            if (accountName) {
                document.title = `${accountName} - ${page}`;
            }
        }

        const hash = window.location.hash;
        if (hash) {
            const subpage = titleCase(hash.substring(1))
            if (page !== subpage) {
                document.title = `${document.title} - ${subpage}`;
            }
        }
    }

    protected getUserPlatformMessagesFromStorage(): any[] {
        const rawSeenMsgs = this.appState.userPlatformMessages || "[]";
        let seenMsgs: any[] = [];

        try {
            seenMsgs = JSON.parse(rawSeenMsgs) as any[];
        } catch (error) {
        }

        return seenMsgs;
    }

    protected resetPlatformMessageTimer(): void {
        // @ts-ignore
        this.platformMessageTimer = setTimeout(
            () => this.updatePlatformMessages(),
            60 * 1000,
        );
    }

    protected async updatePlatformMessages(): Promise<void> {
        if (!this.fetchingPlatformMessages) {
            this.appState.platformMessages = await this.fetchPlatformMessages({ active: true });
        }

        this.resetPlatformMessageTimer();
    }

    protected updateDomainInPlatformPath(domain: DomainDto): void {
        const currentDomainPath = this.currentRoute;
        const newDomainPath = currentDomainPath.replace(/\/?domains\/[\d]+\/?/i, `/domains/${domain.id}/`);
        if (newDomainPath !== currentDomainPath) {
            this.route(newDomainPath);
        }
    }

}

